import { api } from '@udm/contracts';
import { request } from './client';

/** REST client of the operations modules (alarms, projects, groups, reports, GIS, inventory, integrations). */
const qs = (params: Record<string, string | number | undefined | null>) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
};

// ---- alarms
export type AlarmFilter = {
  state?: string;
  severity?: string;
  category?: string;
  type?: string;
  site_id?: string;
  device_id?: string;
  q?: string;
  before?: string;
  limit?: number;
};
export const fetchAlarmPage = (f: AlarmFilter) =>
  request('GET', `/api/v1/alarms${qs(f)}`, undefined, (v) => api.AlarmPage.parse(v));
export const fetchAlarmSummary = (siteId?: string | null) =>
  request('GET', `/api/v1/alarms/summary${qs({ site_id: siteId })}`, undefined, (v) =>
    api.AlarmSummary.parse(v),
  );
export const acknowledgeAlarm = (id: string, note?: string) =>
  request<void>('POST', `/api/v1/alarms/${id}/acknowledge`, note ? { note } : {});
export const closeAlarm = (id: string, note?: string) =>
  request<void>('POST', `/api/v1/alarms/${id}/close`, note ? { note } : {});
export const fetchAlarmTypes = (siteId?: string | null) =>
  request('GET', `/api/v1/alarm-types${qs({ site_id: siteId })}`, undefined, (v) =>
    api.AlarmTypeList.parse(v),
  );
export const upsertAlarmPolicy = (b: api.AlarmPolicyUpsert) =>
  request<void>('PUT', '/api/v1/alarm-policies', b);
export const deleteAlarmPolicy = (id: string) => request<void>('DELETE', `/api/v1/alarm-policies/${id}`);
export const fetchDeviceFaults = (deviceId: string) =>
  request('GET', `/api/v1/devices/${deviceId}/faults`, undefined, (v) => api.DeviceFaultList.parse(v));

// ---- projects
export const fetchProjects = (clientId?: string | null) =>
  request('GET', `/api/v1/projects${qs({ client_id: clientId })}`, undefined, (v) =>
    api.ProjectList.parse(v),
  );
export const createProject = (b: api.ProjectCreate) =>
  request('POST', '/api/v1/projects', b, (v) => api.Project.parse(v));
export const updateProject = (id: string, b: api.ProjectUpdate) =>
  request('PATCH', `/api/v1/projects/${id}`, b, (v) => api.Project.parse(v));
export const deleteProject = (id: string) => request<void>('DELETE', `/api/v1/projects/${id}`);
export const fetchBaselines = (id: string) =>
  request('GET', `/api/v1/projects/${id}/baselines`, undefined, (v) => ({
    items: (v as { items: unknown[] }).items.map((x) => api.EnergyBaseline.parse(x)),
  }));
export const createBaseline = (id: string, b: api.EnergyBaselineCreate) =>
  request('POST', `/api/v1/projects/${id}/baselines`, b, (v) => api.EnergyBaseline.parse(v));
export const fetchProjectEnergy = (id: string, from?: string, to?: string) =>
  request('GET', `/api/v1/projects/${id}/energy${qs({ from, to })}`, undefined, (v) =>
    api.EnergySavings.parse(v),
  );
// serial lists (U20)
export const fetchProjectSerials = (id: string) =>
  request('GET', `/api/v1/projects/${id}/serials`, undefined, (v) => api.ProjectSerialList.parse(v));
export const fetchSerialImport = (id: string) =>
  request('GET', `/api/v1/projects/${id}/serial-import`, undefined, (v) => api.SerialImport.parse(v));
export const createSerialImport = (id: string, text: string) =>
  request('POST', `/api/v1/projects/${id}/serial-import`, { text }, (v) => api.SerialImport.parse(v));
export const resolveSerialImport = (
  id: string,
  resolutions: api.SerialImportResolve['resolutions'],
  leadingZero?: boolean,
) =>
  request(
    'PATCH',
    `/api/v1/projects/${id}/serial-import`,
    { resolutions, ...(leadingZero !== undefined ? { leading_zero: leadingZero } : {}) },
    (v) => api.SerialImport.parse(v),
  );
export const applySerialImport = (id: string) =>
  request('POST', `/api/v1/projects/${id}/serial-import/apply`, undefined, (v) =>
    api.SerialImportResult.parse(v),
  );
export const cancelSerialImport = (id: string) =>
  request<void>('DELETE', `/api/v1/projects/${id}/serial-import`);

// ---- reports
export const fetchReportOverview = (clientId: string, projectId?: string | null) =>
  request(
    'GET',
    `/api/v1/reports/overview${qs({ client_id: clientId, project_id: projectId })}`,
    undefined,
    (v) => api.ReportOverview.parse(v),
  );
export const fetchReports = (clientId?: string | null, type?: string) =>
  request('GET', `/api/v1/reports${qs({ client_id: clientId, type })}`, undefined, (v) =>
    api.ReportList.parse(v),
  );
export const fetchReportQuota = () =>
  request('GET', '/api/v1/reports/quota', undefined, (v) => api.ReportQuota.parse(v));
export const fetchReport = (id: string) =>
  request('GET', `/api/v1/reports/${id}`, undefined, (v) => api.Report.parse(v));
export const generateReport = (b: api.ReportCreate) =>
  request('POST', '/api/v1/reports', b, (v) => api.Report.parse(v));
export const deleteReport = (id: string) => request<void>('DELETE', `/api/v1/reports/${id}`);
export const reportFileUrl = (id: string, format: string) => `/api/v1/reports/${id}/files/${format}`;
export const fetchLightPoints = (clientId: string, projectId?: string | null, siteId?: string | null) =>
  request(
    'GET',
    `/api/v1/reports/light-points${qs({ client_id: clientId, project_id: projectId, site_id: siteId })}`,
    undefined,
    (v) => api.LightPointList.parse(v),
  );

/** Polls a report until it is ready or failed (generation runs in the background on the server). */
export async function waitForReport(id: string, timeoutMs = 120_000): Promise<api.Report> {
  const start = Date.now();
  for (;;) {
    const r = await fetchReport(id);
    if (r.status !== 'pending' || Date.now() - start > timeoutMs) return r;
    await new Promise((res) => setTimeout(res, 800));
  }
}

// ---- operations settings (retention, automatic reports)
export interface OperationsSettings {
  raw_retention_days: number | null;
  presence_retention_days: number | null;
  reports_automatic: { annual: boolean; monthly: boolean; locale: 'en' | 'ro' };
  /** default per-user command rate limit of client users (a client may override it) */
  command_rate_limit?: api.CommandRateLimit;
}
export const fetchOperationsSettings = () =>
  request<OperationsSettings>('GET', '/api/v1/settings/operations');
export const saveOperationsSettings = (b: OperationsSettings) =>
  request<void>('PUT', '/api/v1/settings/operations', b);

// ---- groups, programs, scenes
export const fetchGroups = (clientId?: string | null) =>
  request('GET', `/api/v1/groups${qs({ client_id: clientId })}`, undefined, (v) => api.GroupList.parse(v));
export const fetchGroup = (id: string) =>
  request('GET', `/api/v1/groups/${id}`, undefined, (v) => api.GroupDetail.parse(v));
export const saveGroup = (id: string | null, b: api.GroupSave) =>
  request<{ id: string }>(id ? 'PUT' : 'POST', id ? `/api/v1/groups/${id}` : '/api/v1/groups', b);
export const deleteGroup = (id: string) => request<void>('DELETE', `/api/v1/groups/${id}`);
export const addGroupMembers = (id: string, deviceIds: string[]) =>
  request<void>('PUT', `/api/v1/groups/${id}/members`, { device_ids: deviceIds });
export const removeGroupMembers = (id: string, deviceIds: string[]) =>
  request<void>('DELETE', `/api/v1/groups/${id}/members`, { device_ids: deviceIds });
export const addCalendarRule = (id: string, b: api.CalendarRuleSave) =>
  request<{ id: string }>('POST', `/api/v1/groups/${id}/calendar`, b);
export const deleteCalendarRule = (id: string, rule: string) =>
  request<void>('DELETE', `/api/v1/groups/${id}/calendar/${rule}`);
export const addGroupEvent = (id: string, b: api.GroupEventSave) =>
  request<{ id: string }>('POST', `/api/v1/groups/${id}/events`, b);
export const cancelGroupEvent = (id: string, event: string) =>
  request<void>('DELETE', `/api/v1/groups/${id}/events/${event}`);
export const fetchPrograms = (clientId?: string | null) =>
  request('GET', `/api/v1/programs${qs({ client_id: clientId })}`, undefined, (v) =>
    api.ProgramList.parse(v),
  );
export const saveProgram = (id: string | null, b: api.ProgramSave) =>
  request<{ id: string }>(id ? 'PUT' : 'POST', id ? `/api/v1/programs/${id}` : '/api/v1/programs', b);
export const deleteProgram = (id: string) => request<void>('DELETE', `/api/v1/programs/${id}`);
export const previewProgram = (steps: api.ProgramStep[], clientId?: string | null, nights = 1) =>
  request('POST', `/api/v1/programs/preview${qs({ client_id: clientId, nights })}`, { steps }, (v) =>
    api.ProgramPreview.parse(v),
  );
export const fetchScenes = (clientId?: string | null) =>
  request('GET', `/api/v1/scenes${qs({ client_id: clientId })}`, undefined, (v) => api.SceneList.parse(v));
export const saveScene = (id: string | null, b: api.SceneSave) =>
  request<{ id: string }>(id ? 'PUT' : 'POST', id ? `/api/v1/scenes/${id}` : '/api/v1/scenes', b);
export const deleteScene = (id: string) => request<void>('DELETE', `/api/v1/scenes/${id}`);
export const runScene = (id: string) =>
  request('POST', `/api/v1/scenes/${id}/run`, {}, (v) => api.SceneRunResult.parse(v));

// ---- automation rules (signals → light levels)
export const fetchRules = (clientId?: string | null) =>
  request('GET', `/api/v1/automation-rules${qs({ client_id: clientId })}`, undefined, (v) =>
    api.AutomationRuleList.parse(v),
  );
export const saveRule = (id: string | null, b: api.AutomationRuleSave) =>
  request<{ id: string }>(
    id ? 'PUT' : 'POST',
    id ? `/api/v1/automation-rules/${id}` : '/api/v1/automation-rules',
    b,
  );
export const deleteRule = (id: string) => request<void>('DELETE', `/api/v1/automation-rules/${id}`);

// ---- GIS assets
export const fetchAssetTypes = () =>
  request('GET', '/api/v1/asset-types', undefined, (v) => api.AssetTypeList.parse(v));
export const fetchAssets = (p: {
  client_id?: string | null;
  site_id?: string | null;
  type?: string;
  q?: string;
}) => request('GET', `/api/v1/assets${qs(p)}`, undefined, (v) => api.AssetList.parse(v));
export const fetchAsset = (id: string) =>
  request('GET', `/api/v1/assets/${id}`, undefined, (v) => api.SubjectDetail.parse(v));
export const fetchDeviceRecords = (id: string) =>
  request('GET', `/api/v1/devices/${id}/records`, undefined, (v) => api.SubjectDetail.parse(v));
export const createAsset = (b: api.AssetSave) =>
  request('POST', '/api/v1/assets', b, (v) => api.Asset.parse(v));
export const updateAsset = (id: string, b: api.AssetSave) =>
  request('PUT', `/api/v1/assets/${id}`, b, (v) => api.Asset.parse(v));
export const deleteAsset = (id: string) => request<void>('DELETE', `/api/v1/assets/${id}`);
export const addRelation = (b: api.RelationCreate) =>
  request<{ id: string }>('POST', '/api/v1/assets/relations', b);
export const deleteRelation = (id: string) => request<void>('DELETE', `/api/v1/assets/relations/${id}`);
export const addSubjectEvent = (b: api.SubjectEventCreate) =>
  request<{ id: string }>('POST', '/api/v1/assets/events', b);
export async function uploadDocument(
  subjectType: 'asset' | 'device',
  subjectId: string,
  file: File,
  note?: string,
) {
  const buf = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return request<{ id: string }>('POST', '/api/v1/assets/documents', {
    subject_type: subjectType,
    subject_id: subjectId,
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    data_base64: btoa(bin),
    ...(note ? { note } : {}),
  });
}
export const documentUrl = (id: string) => `/api/v1/assets/documents/${id}`;
export const deleteDocument = (id: string) => request<void>('DELETE', `/api/v1/assets/documents/${id}`);
export const importAssets = (b: {
  client_id: string;
  site_id?: string | null;
  type_key: string;
  name_property?: string;
  code_property?: string;
  features: unknown[];
}) => request('POST', '/api/v1/assets/import', b, (v) => api.AssetImportResult.parse(v));

// ---- inventory
export const fetchInventoryTree = () =>
  request('GET', '/api/v1/inventory/tree', undefined, (v) => api.InventoryTree.parse(v));
export const fetchInventory = (siteId: string, q?: string) =>
  request('GET', `/api/v1/inventory${qs({ site_id: siteId, q })}`, undefined, (v) =>
    api.InventoryList.parse(v),
  );
export const updateInventory = (deviceId: string, b: api.InventoryUpdate) =>
  request<void>('PATCH', `/api/v1/inventory/devices/${deviceId}`, b);

// ---- integrations
export const fetchApiKeys = () =>
  request('GET', '/api/v1/api-keys', undefined, (v) => api.ApiKeyList.parse(v));
export const createApiKey = (b: api.ApiKeyCreate) =>
  request('POST', '/api/v1/api-keys', b, (v) => api.ApiKeyCreated.parse(v));
export const revokeApiKey = (id: string) => request<void>('DELETE', `/api/v1/api-keys/${id}`);
export const fetchMetricCatalog = () =>
  request('GET', '/api/v1/metrics/catalog', undefined, (v) => api.MetricCatalog.parse(v));
export const exportDevicesUrl = (format: 'csv' | 'geojson', clientId?: string | null) =>
  `/api/v1/export/devices${qs({ format, client_id: clientId })}`;

// ---- report profiles (docs/architecture/18-report-profiles.md) ----
export const fetchReportCatalog = () =>
  request('GET', '/api/v1/report-profiles/catalog', undefined, (v) => api.ReportCatalog.parse(v));
export const fetchReportProfiles = (clientId?: string | null) =>
  request('GET', `/api/v1/report-profiles${qs({ client_id: clientId })}`, undefined, (v) =>
    api.ReportProfileList.parse(v),
  );
export const saveReportProfile = (id: string | null, b: api.ReportProfileSave) =>
  request<{ id: string }>(
    id ? 'PUT' : 'POST',
    id ? `/api/v1/report-profiles/${id}` : '/api/v1/report-profiles',
    b,
  );
export const deleteReportProfile = (id: string) => request<void>('DELETE', `/api/v1/report-profiles/${id}`);
export const fetchReportAssignments = (id: string) =>
  request('GET', `/api/v1/report-profiles/${id}/assignments`, undefined, (v) =>
    api.ReportProfileAssignments.parse(v),
  );
export const assignReportProfile = (id: string, t: api.ReportProfileTargets) =>
  request<void>('PUT', `/api/v1/report-profiles/${id}/assignments`, t);
export const unassignReportProfile = (id: string, t: api.ReportProfileTargets) =>
  request<void>('DELETE', `/api/v1/report-profiles/${id}/assignments`, t);
export const fetchReportDevices = (q: {
  clientId: string;
  protocol: string;
  profileId?: string | null;
  status?: string | null;
  search?: string | null;
  limit?: number;
}) =>
  request(
    'GET',
    `/api/v1/report-profiles/devices${qs({
      client_id: q.clientId,
      protocol: q.protocol,
      profile_id: q.profileId,
      status: q.status,
      search: q.search,
      limit: q.limit,
    })}`,
    undefined,
    (v) => api.ReportDeviceList.parse(v),
  );
export const retryReportDevices = (deviceIds: string[]) =>
  request<{ retried: number }>('POST', '/api/v1/report-profiles/devices/retry', { device_ids: deviceIds });
